import { useEffect, useMemo, useRef, useState } from "react";
import {
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";

import { httpsCallable } from "firebase/functions";

import { db, functions } from "../../FirebaseConfig";
import { SavingOverlay } from "../../components/SavingOverlay";
import { useCollection } from "../../lib/firestore-hooks";
import type { CuentaDoc, UsuarioDoc } from "../../lib/types";

interface CuentaRow extends CuentaDoc {
  id: string;
}

/**
 * /admin/accounts equivalent. Cuentas are the billing entities flights
 * get charged to (e.g., "AJB"). Doc id == account code (uppercase).
 */
export function AdminCuentas() {
  const [rows, setRows] = useState<CuentaRow[] | null>(null);
  const [editing, setEditing] = useState<CuentaRow | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const q = query(collection(db, "Cuentas"), orderBy("code"));
    const unsub = onSnapshot(q, (snap) => {
      const list: CuentaRow[] = [];
      snap.forEach((d) =>
        list.push({ id: d.id, ...(d.data() as CuentaDoc) })
      );
      setRows(list);
    });
    return () => unsub();
  }, []);

  if (rows === null) {
    return <p className="text-sm text-slate-500">Cargando…</p>;
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mb-2 h-[3px] w-8 rounded-full bg-brand-700" />
          <h1 className="text-2xl font-semibold tracking-tight text-brand-900">Cuentas</h1>
          <p className="mt-1 text-sm text-slate-600">
            Administrar códigos de cuenta. La cuenta corresponde a la
            entidad responsable de los costos del vuelo.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-900"
        >
          + Nueva cuenta
        </button>
      </header>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Código</th>
              <th className="px-3 py-2">Nombre</th>
              <th className="px-3 py-2">Email contacto</th>
              <th className="px-3 py-2 text-center">Activa</th>
              <th className="px-3 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr
                key={r.id}
                onClick={() => setEditing(r)}
                className="cursor-pointer hover:bg-sky-50"
              >
                <td className="px-3 py-2 font-mono text-slate-700">{r.code}</td>
                <td className="px-3 py-2 text-slate-900">{r.displayName}</td>
                <td className="px-3 py-2 text-xs text-slate-600">
                  {r.contactEmails.join(", ") || "—"}
                </td>
                <td className="px-3 py-2 text-center">
                  {r.active ? "✓" : "—"}
                </td>
                <td
                  className="px-3 py-2 text-right"
                  onClick={(e) => e.stopPropagation()}
                >
                  <RenameCuentaButton code={r.code} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing ? (
        <CuentaEditor initial={editing} onClose={() => setEditing(null)} />
      ) : null}
      {creating ? (
        <CuentaEditor initial={null} onClose={() => setCreating(false)} />
      ) : null}
    </div>
  );
}

function CuentaEditor({
  initial,
  onClose,
}: {
  initial: CuentaRow | null;
  onClose: () => void;
}) {
  const isNew = initial === null;
  const [code, setCode] = useState(initial?.code ?? "");
  const [displayName, setDisplayName] = useState(initial?.displayName ?? "");
  const [emails, setEmails] = useState(initial?.contactEmails.join(", ") ?? "");
  const [active, setActive] = useState(initial?.active ?? true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Staged user grants for new-cuenta creation. After the cuenta doc
  // is written, we fan out arrayUnion(code) to each staged user.
  const [pendingGrantUids, setPendingGrantUids] = useState<string[]>([]);

  async function save() {
    setError(null);
    setSaving(true);
    try {
      const c = code.trim().toUpperCase();
      if (!/^[A-Z0-9]{2,8}$/.test(c)) {
        throw new Error("Código debe ser 2-8 caracteres alfanuméricos.");
      }
      const name = displayName.trim();
      if (!name) throw new Error("Nombre obligatorio.");
      const contactEmails = emails
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const data = { code: c, displayName: name, contactEmails, active };
      const ref = doc(db, "Cuentas", c);
      if (isNew) {
        await setDoc(ref, { ...data, createdAt: serverTimestamp() });
        // Fan out the staged grants now that the cuenta exists.
        for (const uid of pendingGrantUids) {
          await updateDoc(doc(db, "Usuarios", uid), {
            accountCodes: arrayUnion(c),
            updatedAt: serverTimestamp(),
          });
        }
      } else {
        await updateDoc(ref, data);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4"
      onClick={onClose}
    >
      <SavingOverlay open={saving} label="Guardando…" />
      <div
        className="my-8 w-full max-w-md rounded-lg bg-white p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-slate-900">
          {isNew ? "Nueva cuenta" : `Editar ${initial!.code}`}
        </h2>
        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Código
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              disabled={!isNew}
              maxLength={8}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm uppercase focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50 disabled:bg-slate-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Nombre
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Email contacto
            </label>
            <input
              type="text"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              placeholder="ejemplo@dominio.cl, otro@dominio.cl"
              title="Acepta varias direcciones separadas por coma."
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="rounded border-slate-300"
            />
            Activa
          </label>
          {!isNew ? (
            <UsersWithAccessSection code={initial!.code} />
          ) : (
            <StagedUsersWithAccess
              uids={pendingGrantUids}
              onChange={setPendingGrantUids}
            />
          )}
          {error ? (
            <p className="rounded border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
              {error}
            </p>
          ) : null}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-brand-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={save}
            className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-900 disabled:opacity-50"
          >
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Inverse-direction grant list: shows every Usuario whose accountCodes
 * explicitly includes this code, with a chip-style remove (×). Below
 * the chips is a one-at-a-time typeahead picker for adding access.
 *
 * Unrestricted users (accountCodes = null / []) get acknowledged as a
 * count rather than appearing in the picker — granting them this code
 * explicitly would actually *reduce* their access (from "all accounts"
 * to "just this one"), which isn't what an admin clicking + Agregar
 * expects.
 */
function UsersWithAccessSection({ code }: { code: string }) {
  const { rows: usuarios } = useCollection<UsuarioDoc>(
    "Usuarios",
    orderBy("displayName")
  );

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const { granted, unrestrictedCount, addable } = useMemo(() => {
    const list = (usuarios ?? []).filter((u) => !u.isDeleted);
    const granted: (UsuarioDoc & { id: string })[] = [];
    let unrestrictedCount = 0;
    const addable: (UsuarioDoc & { id: string })[] = [];
    for (const u of list) {
      const codes = Array.isArray(u.accountCodes) ? u.accountCodes : null;
      const isAdmin = u.isAdmin || u.isSuperAdmin;
      const explicitlyGranted = !!codes && codes.includes(code);
      const unrestricted = !codes || codes.length === 0;
      if (explicitlyGranted) {
        granted.push(u);
      } else if (unrestricted) {
        // Admins always have access; non-admins are unrestricted if
        // they have no accountCodes set. Count both as "implicit
        // access" so the admin understands why they're not in the
        // explicit list.
        if (isAdmin || !codes) unrestrictedCount++;
        addable.push(u);
      } else {
        addable.push(u);
      }
    }
    return { granted, unrestrictedCount, addable };
  }, [usuarios, code]);

  const term = query.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!term) return addable.slice(0, 8);
    return addable
      .filter((u) => {
        const hay = `${u.displayName ?? ""} ${u.email ?? ""}`.toLowerCase();
        return hay.includes(term);
      })
      .slice(0, 8);
  }, [addable, term]);

  async function add(u: UsuarioDoc & { id: string }) {
    setBusy(true);
    try {
      const current = Array.isArray(u.accountCodes) ? u.accountCodes : [];
      // If the user was unrestricted (empty/null), explicit-listing this
      // single code would narrow their access. Confirm first.
      if (current.length === 0) {
        const ok = window.confirm(
          `${u.displayName ?? u.email ?? u.id} no tiene restricción de cuentas (puede usar todas). ` +
            `Al agregar ${code}, quedará restringido SOLO a las cuentas que listemos aquí. ¿Continuar?`
        );
        if (!ok) {
          setBusy(false);
          return;
        }
      }
      await updateDoc(doc(db, "Usuarios", u.id), {
        accountCodes: arrayUnion(code),
        updatedAt: serverTimestamp(),
      });
      setQuery("");
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  async function remove(u: UsuarioDoc & { id: string }) {
    setBusy(true);
    try {
      await updateDoc(doc(db, "Usuarios", u.id), {
        accountCodes: arrayRemove(code),
        updatedAt: serverTimestamp(),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <label className="block text-sm font-medium text-slate-700">
        Usuarios con acceso
      </label>
      <p className="mt-1 text-xs text-slate-500">
        Los cambios se guardan inmediatamente.
      </p>

      {granted.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {granted.map((u) => (
            <li
              key={u.id}
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-800"
            >
              <span>{u.displayName ?? u.email ?? u.id}</span>
              <button
                type="button"
                onClick={() => remove(u)}
                disabled={busy}
                className="ml-0.5 text-slate-500 hover:text-rose-700 disabled:opacity-50"
                aria-label="Quitar acceso"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-slate-500">
          Sin usuarios con acceso explícito.
        </p>
      )}

      <div ref={containerRef} className="relative mt-3">
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (matches[0]) add(matches[0]);
            } else if (e.key === "Escape") setOpen(false);
          }}
          disabled={busy}
          placeholder="+ Agregar usuario…"
          className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50 disabled:opacity-50"
        />
        {open ? (
          <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
            {matches.length === 0 ? (
              <li className="px-3 py-2 text-xs text-slate-500">
                Sin coincidencias.
              </li>
            ) : (
              matches.map((u) => (
                <li
                  key={u.id}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    add(u);
                  }}
                  className="cursor-pointer px-3 py-1.5 text-sm text-slate-800 hover:bg-sky-100"
                >
                  <span>{u.displayName ?? "(sin nombre)"}</span>
                  {u.email ? (
                    <span className="ml-2 text-xs text-slate-500">
                      {u.email}
                    </span>
                  ) : null}
                </li>
              ))
            )}
          </ul>
        ) : null}
      </div>

      {unrestrictedCount > 0 ? (
        <p className="mt-2 text-xs text-slate-500">
          {unrestrictedCount} usuario{unrestrictedCount > 1 ? "s" : ""} sin
          restricción también tiene{unrestrictedCount > 1 ? "n" : ""} acceso a
          todas las cuentas.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Staging variant of UsersWithAccessSection used while creating a new
 * cuenta. The cuenta doc doesn't exist yet, so we can't write to
 * users' accountCodes here. Instead we collect the chosen UIDs and let
 * CuentaEditor.save() fan them out once the cuenta is persisted.
 */
function StagedUsersWithAccess({
  uids,
  onChange,
}: {
  uids: string[];
  onChange: (next: string[]) => void;
}) {
  const { rows: usuarios } = useCollection<UsuarioDoc>(
    "Usuarios",
    orderBy("displayName")
  );
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const all = (usuarios ?? []).filter((u) => !u.isDeleted);
  const stagedSet = new Set(uids);
  const granted = all.filter((u) => stagedSet.has(u.id));
  const addable = all.filter((u) => !stagedSet.has(u.id));
  const term = query.trim().toLowerCase();
  const matches = (
    !term
      ? addable
      : addable.filter((u) => {
          const hay = `${u.displayName ?? ""} ${u.email ?? ""}`.toLowerCase();
          return hay.includes(term);
        })
  ).slice(0, 8);

  function add(uid: string) {
    if (uids.includes(uid)) return;
    onChange([...uids, uid]);
    setQuery("");
    setOpen(false);
  }
  function remove(uid: string) {
    onChange(uids.filter((u) => u !== uid));
  }

  return (
    <div>
      <label className="block text-sm font-medium text-slate-700">
        Usuarios con acceso (opcional)
      </label>
      <p className="mt-1 text-xs text-slate-500">
        Se asignan al guardar la cuenta. Puedes dejarlo vacío y agregar
        después.
      </p>

      {granted.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {granted.map((u) => (
            <li
              key={u.id}
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-800"
            >
              <span>{u.displayName ?? u.email ?? u.id}</span>
              <button
                type="button"
                onClick={() => remove(u.id)}
                className="ml-0.5 text-slate-500 hover:text-rose-700"
                aria-label="Quitar"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div ref={containerRef} className="relative mt-3">
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (matches[0]) add(matches[0].id);
            } else if (e.key === "Escape") setOpen(false);
          }}
          placeholder="+ Agregar usuario…"
          className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
        />
        {open ? (
          <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
            {matches.length === 0 ? (
              <li className="px-3 py-2 text-xs text-slate-500">
                Sin coincidencias.
              </li>
            ) : (
              matches.map((u) => (
                <li
                  key={u.id}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    add(u.id);
                  }}
                  className="cursor-pointer px-3 py-1.5 text-sm text-slate-800 hover:bg-sky-100"
                >
                  <span>{u.displayName ?? "(sin nombre)"}</span>
                  {u.email ? (
                    <span className="ml-2 text-xs text-slate-500">
                      {u.email}
                    </span>
                  ) : null}
                </li>
              ))
            )}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Small "Renombrar" row action. Pops a window.prompt for the new
 * code, confirms the destructive rewrite, then invokes the
 * `renameCuenta` callable. On success the table re-renders via the
 * existing Cuentas onSnapshot subscription.
 */
function RenameCuentaButton({ code }: { code: string }) {
  const [busy, setBusy] = useState(false);
  async function rename() {
    const next = window.prompt(
      `Renombrar cuenta ${code}. Nuevo código (2-16 letras/números):`,
      code
    );
    if (next == null) return;
    const trimmed = next.trim().toUpperCase();
    if (!trimmed || trimmed === code) return;
    const ok = window.confirm(
      `Esto reescribirá ${trimmed} en todos los vuelos + usuarios que referencian ${code}. ¿Continuar?`
    );
    if (!ok) return;
    setBusy(true);
    try {
      const call = httpsCallable<
        { fromCode: string; toCode: string },
        { flightsUpdated: number; usersUpdated: number }
      >(functions, "renameCuenta");
      const res = await call({ fromCode: code, toCode: trimmed });
      window.alert(
        `Listo. Vuelos: ${res.data.flightsUpdated}. Usuarios: ${res.data.usersUpdated}.`
      );
    } catch (err) {
      window.alert(
        `Error: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <SavingOverlay open={busy} label="Renombrando cuenta…" />
      <button
        type="button"
        disabled={busy}
        onClick={rename}
        className="text-xs text-sky-700 hover:text-sky-900 disabled:opacity-50"
        title="Cambiar el código de esta cuenta y reescribir todas las referencias"
      >
        {busy ? "Renombrando…" : "Renombrar"}
      </button>
    </>
  );
}
